import {
  compareMoney,
  currencyScale,
  fromMinorUnits,
  isMoneyString,
  isZeroMoney,
  parseMoney,
  toMinorUnits,
} from "@ibada/shared-types/finance"

import { currencySymbol, formatCurrency, type DisplayAmount } from "@/shared/lib/formatting/currency"
import { formatDate as formatDateDisplay } from "@/shared/lib/dates/format-date"
import {
  CurrencyCode,
  type FinanceAccount,
  FinanceCurrency,
  type MoneyText,
} from "@/features/finance/model/finance-types"

/**
 * Renders money. Takes the canonical decimal STRING the API sends (IBA-116) and hands it to `Intl`, which
 * parses it exactly — a `number` is still accepted for the values that genuinely are numbers (chart geometry),
 * but money should reach here as it left the server.
 */
export function formatMoney(amount: DisplayAmount, currency: FinanceCurrency | CurrencyCode | string) {
  return formatCurrency(amount, currency)
}

/**
 * The magnitude of a money value, as a STRING.
 *
 * `Math.abs()` would drop the value into a float first — the exact conversion this module exists to avoid — and
 * a sign is not arithmetic: it is the first character. Stripping it is exact for every amount, however large.
 */
export function absMoney(amount: DisplayAmount): DisplayAmount {
  if (typeof amount === "number") return Math.abs(amount)
  const trimmed = amount.trim()
  return trimmed.startsWith("-") ? trimmed.slice(1) : trimmed
}

/**
 * Whether a money value is negative — a deficit, an overdraft, a debit balance.
 *
 * The sign of a decimal string is its FIRST CHARACTER, so this needs no currency, does no parsing, and cannot
 * throw. `Number(value) < 0` would have to build a float to answer a question the string already answers, and
 * `compareMoney` would need the currency and would reject a value whose scale disagreed with it — a crash in a
 * renderer, over a minus sign.
 */
export function isNegativeMoney(amount: DisplayAmount): boolean {
  if (typeof amount === "number") return amount < 0
  const trimmed = amount.trim()
  // `-0.00` is zero, not a deficit.
  return trimmed.startsWith("-") && /[1-9]/.test(trimmed)
}

/**
 * Zero or negative — an account with nothing to send, a fund with no target worth showing progress against.
 *
 * Exact, on integer minor units. The `amount <= 0` it replaces asked the question of a float.
 */
export function isZeroOrNegativeMoney(amount: string, currency: string): boolean {
  return isNegativeMoney(amount) || isZeroMoney(amount, currency)
}

/* -------------------------------------------------------------------------- */
/* The WRITE boundary (IBA-116) — money the user types, on its way to the API   */
/* -------------------------------------------------------------------------- */

/**
 * What a money input HOLDS: the decimal text the user typed, exactly as they typed it.
 *
 * There is no `number` anywhere on this path any more. `moneyToFormNumber` used to live here — a documented
 * float doorway that existed only because the API's write contract was `t.Number()`. The contract now takes a
 * canonical decimal string, so the doorway is gone: the characters the operator types are the characters the
 * ledger stores.
 *
 * A half-typed value (`""`, `"12."`, `"-"`) is a legitimate state of this type — it is text in a field, not
 * money yet. `toMoneyOrNull` is the one place that decides whether it has become money.
 */
export type MoneyFieldValue = string

/** Seed a money input from a value the API sent. It is already a canonical string; `""` when there is nothing. */
export function moneyToFormValue(amount: MoneyText | null | undefined): MoneyFieldValue {
  if (amount === null || amount === undefined) return ""
  return amount.trim()
}

/**
 * The canonical money a half-typed field currently holds, or `null` when it is not money (yet).
 *
 * Returns `null` — rather than throwing or guessing — for `""`, `"1.2.3"`, `"-"`, and for a value carrying more
 * decimals than the currency has (`"10.005"` in USD is not 1000 cents and is not 1001 cents; it is not an amount).
 * Every caller below is a live preview reacting to a keystroke, so "not money yet" must be an ordinary answer.
 */
export function toMoneyOrNull(
  value: MoneyFieldValue | null | undefined,
  currency: string,
): MoneyText | null {
  // `typeof` rather than a null check alone: a Zod object-level refinement runs even when a FIELD failed, and the
  // failed field is not a string at that point. A money helper that throws inside a validator would take the whole
  // form down over a half-typed amount.
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed || !isMoneyString(trimmed)) return null
  try {
    return parseMoney(trimmed, currency)
  } catch {
    // Excess precision for this currency — real text, but not an amount in this ledger.
    return null
  }
}

/**
 * The exact sum of money fields, in integer minor units.
 *
 * `values.reduce((sum, v) => sum + Number(v), 0)` is what this replaces: three lines of `33.33 + 33.33 + 33.34`
 * add up to `100.00000000000001` in IEEE-754, which then fails an equality check against a `100.00` total. There
 * is no epsilon to tune here — the addition is integer `bigint` arithmetic, so the answer is simply right.
 *
 * Values that are not money yet contribute nothing (an empty split slot is not a zero the user asserted).
 */
export function sumMoney(values: ReadonlyArray<MoneyFieldValue | null | undefined>, currency: string): MoneyText {
  let total = 0n
  for (const value of values) {
    const money = toMoneyOrNull(value, currency)
    if (money !== null) total += toMinorUnits(money, currency)
  }
  return fromMinorUnits(total, currency)
}

/** `a − b`, exactly. Both must already be money (use `toMoneyOrNull` on anything a user is still typing). */
export function subtractMoney(a: MoneyText, b: MoneyText, currency: string): MoneyText {
  return fromMinorUnits(toMinorUnits(a, currency) - toMinorUnits(b, currency), currency)
}

/** The smaller of two amounts — used to cap an allocation at an account's balance. Compared exactly. */
export function minMoney(a: MoneyText, b: MoneyText, currency: string): MoneyText {
  return compareMoney(a, b, currency) <= 0 ? parseMoney(a, currency) : parseMoney(b, currency)
}

/**
 * The `step` a money input should advance by: the smallest amount the currency HAS.
 *
 * `step="0.01"` was hardcoded on every amount field, which offers centimes in a currency that has none — RWF has
 * no minor unit at all (ISO 4217 exponent 0), so `1500.75 RWF` is not an amount, and the schema now rejects it.
 * An input that lets you type a value the ledger must refuse is an input that lies about what is allowed.
 */
export function getMoneyInputStep(currency: string): string {
  const scale = currencyScale(currency)
  return scale === 0 ? "1" : `0.${"0".repeat(scale - 1)}1`
}

/** Strictly greater than zero. Exact, and `false` for anything that is not money — never `Number(v) > 0`. */
export function isPositiveMoney(value: MoneyFieldValue | null | undefined, currency: string): boolean {
  const money = toMoneyOrNull(value, currency)
  if (money === null) return false
  return toMinorUnits(money, currency) > 0n
}

// The symbol/code shown before a bare money input ("$", "€", "CDF", "RWF"). This is NOT a second definition:
// `currencySymbol` derives it from the SAME shared decision that formats every amount, so an input prefix can
// never disagree with the value beside it. There used to be three hand-written switches here and they drifted
// ("FR" vs "RF" vs "RWF" for the same currency) — never again.
export function getCurrencyPrefix(currency: FinanceCurrency | CurrencyCode | string) {
  return currencySymbol(String(currency ?? ""))
}

export function formatDate(value: string) {
  return formatDateDisplay(value)
}

export function formatDateTime(value: string | null, timeZone?: string) {
  return formatDateDisplay(value, { variant: "datetime", timeZone })
}

export function getAccountLabel(
  accounts: ReadonlyArray<Pick<FinanceAccount, "id" | "name">>,
  accountId: string,
) {
  return accounts.find((entry) => entry.id === accountId)?.name ?? accountId
}

export function csvFromRows(rows: Array<Record<string, unknown>>) {
  if (!rows.length) return ""
  const headers = Object.keys(rows[0])
  const escape = (value: unknown) => {
    const text = String(value ?? "")
    if (text.includes(",") || text.includes("\n") || text.includes('"')) {
      return `"${text.replaceAll('"', '""')}"`
    }
    return text
  }
  const lines = [headers.join(",")]
  for (const row of rows) {
    lines.push(headers.map((key) => escape(row[key])).join(","))
  }
  return lines.join("\n")
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
